import React, { useState, useMemo } from 'react';
import { Search, Filter, Globe, CheckCircle, XCircle, Grid, List } from 'lucide-react';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { StoreCard } from '../components/stores/StoreCard';
import { StoreDetails } from '../components/stores/StoreDetails';
import { useToast } from '../components/common/Toast';
import { allStores } from '../data/mockData';
import { Store, StoreCategory } from '../types';

export const DistributionStores: React.FC = () => {
  const [stores, setStores] = useState<Store[]>(allStores);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<StoreCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'category' | 'processingTime'>('name');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const { addToast } = useToast();

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'streaming', label: 'Streaming' },
    { value: 'download', label: 'Download' },
    { value: 'social', label: 'Social Media' },
    { value: 'radio', label: 'Radio' },
    { value: 'gaming', label: 'Gaming' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const regionOptions = [
    { value: 'all', label: 'All Regions' },
    { value: 'Global', label: 'Global' },
    { value: 'India', label: 'India' },
    { value: 'United States', label: 'United States' },
    { value: 'Europe', label: 'Europe' },
    { value: 'Asia', label: 'Asia' },
    { value: 'Middle East', label: 'Middle East' },
    { value: 'Africa', label: 'Africa' }
  ];

  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'category', label: 'Category' },
    { value: 'processingTime', label: 'Processing Time' }
  ];

  const filteredAndSortedStores = useMemo(() => {
    let filtered = stores.filter(store => {
      const matchesSearch = store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           store.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || store.category === categoryFilter;
      const matchesStatus = statusFilter === 'all' || 
                           (statusFilter === 'active' && store.isActive) ||
                           (statusFilter === 'inactive' && !store.isActive);
      const matchesRegion = regionFilter === 'all' || store.region.includes(regionFilter);
      
      return matchesSearch && matchesCategory && matchesStatus && matchesRegion;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        case 'category':
          return a.category.localeCompare(b.category);
        case 'processingTime':
          // Extract first number from processing time for sorting
          const aTime = parseInt(a.processingTime.match(/\d+/)?.[0] || '0');
          const bTime = parseInt(b.processingTime.match(/\d+/)?.[0] || '0');
          return aTime - bTime;
        default:
          return 0;
      }
    });

    return filtered;
  }, [stores, searchQuery, categoryFilter, statusFilter, regionFilter, sortBy]);

  const handleViewDetails = (store: Store) => {
    setSelectedStore(store);
    setShowDetails(true);
  };

  const handleToggleStatus = (storeId: string) => {
    setStores(prev => prev.map(store => 
      store.id === storeId 
        ? { ...store, isActive: !store.isActive }
        : store
    ));
    
    const store = stores.find(s => s.id === storeId);
    if (store) {
      addToast({
        type: 'success',
        title: `${store.name} ${!store.isActive ? 'Enabled' : 'Disabled'}`,
        message: `Distribution to ${store.name} has been ${!store.isActive ? 'enabled' : 'disabled'}.`
      });
    }
  };

  const getStoreStats = () => {
    const active = stores.filter(s => s.isActive).length;
    const inactive = stores.filter(s => s.isActive === false).length;
    const categories = stores.reduce((acc, store) => {
      acc[store.category] = (acc[store.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return { active, inactive, total: stores.length, categories };
  };

  const stats = getStoreStats();

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center p-4 bg-blue-500/10 border-blue-500/20">
          <div className="text-2xl font-bold text-blue-400 mb-1">
            {stats.total}
          </div>
          <div className="text-sm text-gray-400">Total Stores</div>
        </Card>
        <Card className="text-center p-4 bg-green-500/10 border-green-500/20">
          <div className="text-2xl font-bold text-green-400 mb-1">
            {stats.active}
          </div>
          <div className="text-sm text-gray-400">Active</div>
        </Card>
        <Card className="text-center p-4 bg-red-500/10 border-red-500/20">
          <div className="text-2xl font-bold text-red-400 mb-1">
            {stats.inactive}
          </div>
          <div className="text-sm text-gray-400">Inactive</div>
        </Card>
        <Card className="text-center p-4 bg-purple-500/10 border-purple-500/20">
          <div className="text-2xl font-bold text-purple-400 mb-1">
            {Object.keys(stats.categories).length}
          </div>
          <div className="text-sm text-gray-400">Categories</div>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-white font-display">
              Quick Actions
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Manage your distribution preferences
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setStores(prev => prev.map(store => ({ ...store, isActive: true })));
                addToast({
                  type: 'success',
                  title: 'All Stores Enabled',
                  message: 'Distribution has been enabled for all platforms.'
                });
              }}
              icon={CheckCircle}
            >
              Enable All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setStores(prev => prev.map(store => ({ ...store, isActive: false })));
                addToast({
                  type: 'info',
                  title: 'All Stores Disabled',
                  message: 'Distribution has been disabled for all platforms.'
                });
              }}
              icon={XCircle}
            >
              Disable All
            </Button>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search stores..."
                value={searchQuery}
                onChange={setSearchQuery}
                icon={Search}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Select
                value={categoryFilter}
                onChange={(value) => setCategoryFilter(value as StoreCategory | 'all')}
                options={categoryOptions}
                className="min-w-48"
              />
              <Select
                value={statusFilter}
                onChange={(value) => setStatusFilter(value as 'all' | 'active' | 'inactive')}
                options={statusOptions}
                className="min-w-40"
              />
              <Select
                value={regionFilter}
                onChange={setRegionFilter}
                options={regionOptions}
                className="min-w-40"
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Select
                value={sortBy}
                onChange={(value) => setSortBy(value as any)}
                options={sortOptions}
                className="min-w-40"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gold-500 text-black' : 'text-gray-400 hover:text-white'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-gold-500 text-black' : 'text-gray-400 hover:text-white'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-400">
        <span>
          Showing {filteredAndSortedStores.length} of {stores.length} stores
        </span>
        {(searchQuery || categoryFilter !== 'all' || statusFilter !== 'all' || regionFilter !== 'all') && (
          <button
            onClick={() => {
              setSearchQuery('');
              setCategoryFilter('all');
              setStatusFilter('all');
              setRegionFilter('all');
            }}
            className="text-gold-400 hover:text-gold-300"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Stores Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredAndSortedStores.map((store) => (
            <StoreCard
              key={store.id}
              store={store}
              onViewDetails={handleViewDetails}
              onToggleStatus={handleToggleStatus}
            />
          ))}
        </div>
      ) : (
        <Card padding="none" className="overflow-hidden">
          <div className="divide-y divide-gray-700">
            {filteredAndSortedStores.map((store) => (
              <div key={store.id} className="p-6 hover:bg-gray-800/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="text-2xl">{store.logo}</div>
                    <div>
                      <div className="flex items-center space-x-3 mb-1">
                        <h3 className="text-lg font-semibold text-white">{store.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                          store.category === 'streaming' ? 'text-green-400 bg-green-500/10 border-green-500/20' :
                          store.category === 'download' ? 'text-blue-400 bg-blue-500/10 border-blue-500/20' :
                          store.category === 'social' ? 'text-purple-400 bg-purple-500/10 border-purple-500/20' :
                          'text-gray-400 bg-gray-500/10 border-gray-500/20'
                        }`}>
                          {store.category}
                        </span>
                        {store.isActive ? (
                          <div className="flex items-center space-x-1 text-green-400">
                            <CheckCircle className="w-3 h-3" />
                            <span className="text-xs">Active</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-1 text-red-400">
                            <XCircle className="w-3 h-3" />
                            <span className="text-xs">Inactive</span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-400 line-clamp-1 mb-2">{store.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{store.processingTime}</span>
                        <span>{store.region.join(', ')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(store)}
                    >
                      Details
                    </Button>
                    <Button
                      variant={store.isActive ? "danger" : "primary"}
                      size="sm"
                      onClick={() => handleToggleStatus(store.id)}
                    >
                      {store.isActive ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Empty State */}
      {filteredAndSortedStores.length === 0 && (
        <Card className="text-center p-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-700 rounded-full flex items-center justify-center">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No stores found</h3>
          <p className="text-gray-400 mb-6">
            Try adjusting your filters to see more distribution platforms.
          </p>
          <Button
            onClick={() => {
              setSearchQuery('');
              setCategoryFilter('all');
              setStatusFilter('all');
              setRegionFilter('all');
            }}
          >
            Clear Filters
          </Button>
        </Card>
      )}

      {/* Store Details Modal */}
      <StoreDetails
        store={selectedStore}
        isOpen={showDetails}
        onClose={() => {
          setShowDetails(false);
          setSelectedStore(null);
        }}
      />
    </div>
  );
};